import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '@clofo/i18n'
import { clofoApi, ClofoApiError } from '@clofo/data/api'
import { FormInput } from '@clofo/ui/controlled-field'
import { SubmitButton } from '@clofo/ui/submit-button'
import { Button } from '@clofo/ui/button'

import { getRedirectTo } from '../lib/redirect'

const HELP_CENTRE_URL = 'https://clofo.zendesk.com/hc/en-us/articles/25349614312979'

type OtpFormProps = {
  /**
   * Hide the visible OTP label (it stays available to screen readers) when the surrounding page
   * copy already names the field, as on the confirm page's "Step 3" heading.
   */
  hideLabel?: boolean
}

/** A 400 whose body carries an `otp` key is the backend's wrong-code rejection. */
const isInvalidOtpError = (error: unknown) =>
  error instanceof ClofoApiError &&
  error.status === 400 &&
  typeof error.errorData === 'object' &&
  error.errorData !== null &&
  'otp' in error.errorData

/**
 * OTP entry shared by the /tfa/verify and /tfa/confirm pages: the 6-digit code field, the
 * verify/cancel actions, and the Help Centre link. Submitting exchanges the pending-TFA session
 * for a verified token (tokens/refresh with the otp), then redirects to the login destination.
 */
export const OtpForm = ({ hideLabel }: OtpFormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const schema = z.object({
    otp: z.string().regex(/^\d{6}$/, { error: t('login.tfa.tfaCode') }),
  })
  type Values = z.infer<typeof schema>

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { otp: '' },
  })

  const onSubmit = async ({ otp }: Values) => {
    try {
      await clofoApi.auth.refreshToken(otp)
      window.location.href = getRedirectTo()
    } catch (error) {
      form.setError('otp', {
        message: isInvalidOtpError(error)
          ? t('login.errors.invalid_otp')
          : t('login.errors.generic'),
      })
    }
  }

  const onCancel = async () => {
    try {
      // Ends the pending-TFA session and full-page redirects to the login URL (the email step).
      await clofoApi.auth.logout()
    } catch {
      // The logout endpoint failed — drop the local session and return to the email step ourselves.
      await clofoApi.auth.clearSession()
      navigate('/')
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col'>
      <FormInput
        control={form.control}
        name='otp'
        label={t('login.enterOtp')}
        hideLabel={hideLabel}
        placeholder='XXXXXX'
        inputMode='numeric'
        autoComplete='one-time-code'
        maxLength={6}
        autoFocus
        className={{ input: 'max-w-3xs' }}
      />

      <div className='mt-6 flex flex-wrap items-center justify-between gap-4'>
        <div className='flex gap-3'>
          <SubmitButton
            type='submit'
            isSubmitting={form.formState.isSubmitting}
            className='min-w-30'
          >
            {t('login.verify')}
          </SubmitButton>
          <Button type='button' variant='tertiary' onClick={onCancel} className='min-w-30'>
            {t('common.cancel')}
          </Button>
        </div>
        <a
          className='text-primary hover:underline text-sm'
          href={HELP_CENTRE_URL}
          target='_blank'
          rel='noreferrer noopener'
        >
          {t('login.havingTrouble')}
        </a>
      </div>
    </form>
  )
}
